import { useEffect, useRef, useState } from 'react';
import { PlaceVisitGroup, VisitDetail, placeVisitGroup, visitDetail, visitIndex } from '../types/placeVisits';

interface Projected { revision: string; metadata: string; group: PlaceVisitGroup }
export function usePlaceVisits(placeId: string) {
  const [state, setState] = useState({ placeId: '', groups: [] as PlaceVisitGroup[], loading: false, error: '' });
  const [attempt, retry] = useState(0);
  const cache = useRef(new Map<string, { revision: string; metadata: string; record: VisitDetail }>());
  const projected = useRef({ placeId: '', records: new Map<string, Projected>() });
  useEffect(() => {
    if (!placeId) return;
    if (projected.current.placeId !== placeId) projected.current = { placeId, records: new Map() };
    let alive = true, pending = false, request: AbortController, timeout: number;
    const load = async () => {
      if (pending) return;
      pending = true;
      const controller = new AbortController(); request = controller;
      const current = () => alive && !controller.signal.aborted && request === controller;
      setState(previous => ({ placeId, groups: previous.placeId === placeId ? previous.groups : [], loading: true, error: '' }));
      timeout = window.setTimeout(() => {
        controller.abort(); pending = false;
        if (alive) setState({ placeId, groups: [], loading: false, error: '訪問履歴の読み込みに時間がかかっています。' });
      }, 30000);
      try {
        const response = await fetch('/data/broadcast-index.json', { cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error();
        const entries = visitIndex(await response.json()).filter(entry => entry.places.some(place => place.id === placeId));
        if (!current()) return;
        const next = new Map<string, Projected>();
        let cursor = 0;
        await Promise.all(Array.from({ length: Math.min(3, entries.length) }, async () => {
          while (cursor < entries.length && current()) {
            const entry = entries[cursor++], known = projected.current.records.get(entry.id);
            const metadata = JSON.stringify([entry.date, entry.title, entry.timeBasis, entry.startTime, entry.startTimeApproximate]);
            if (known?.revision === entry.revision && known.metadata !== metadata) throw new Error();
            if (known?.revision === entry.revision) { next.set(entry.id, known); continue; }
            let saved = cache.current.get(entry.id);
            if (saved?.revision === entry.revision && saved.metadata !== metadata) throw new Error();
            if (saved?.revision !== entry.revision) {
              const detail = await fetch(`${entry.path}?revision=${entry.revision}`, { cache: 'no-store', signal: controller.signal });
              if (!detail.ok) throw new Error();
              const bytes = await detail.arrayBuffer(), digest = await crypto.subtle.digest('SHA-256', bytes);
              const revision = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
              if (revision !== entry.revision) throw new Error();
              saved = { revision, metadata, record: visitDetail(JSON.parse(new TextDecoder().decode(bytes)), entry) };
            }
            if (!current()) return;
            cache.current.delete(entry.id); cache.current.set(entry.id, saved);
            if (cache.current.size > 5) cache.current.delete(cache.current.keys().next().value);
            next.set(entry.id, { revision: entry.revision, metadata, group: placeVisitGroup(saved.record, placeId) });
          }
        }));
        if (!current()) return;
        projected.current = { placeId, records: next };
        setState({ placeId, groups: Array.from(next.values()).map(value => value.group).filter(group => group.visits.length), loading: false, error: '' });
      } catch {
        if (current()) {
          controller.abort();
          setState({ placeId, groups: [], loading: false, error: '訪問履歴を読み込めませんでした。' });
        }
      } finally {
        if (request === controller) { window.clearTimeout(timeout); pending = false; }
      }
    };
    load();
    const interval = window.setInterval(load, 60000);
    return () => { alive = false; window.clearInterval(interval); window.clearTimeout(timeout); request?.abort(); };
  }, [placeId, attempt]);
  return { ...(state.placeId === placeId ? state : { groups: [], loading: !!placeId, error: '' }), retry: () => retry(value => value + 1) };
}
